import { buildApiUrl } from '~/app/lib/api-config';
import type {
  DiagnosticRule,
  DiagnosticRuleDetail,
  DiagnosticRuleFilters,
  DiagnosticRuleInput,
  DiagnosticRuleOptions,
  DiagnosticRuleScope,
} from '~/app/services/diagnosticRules/diagnostic-rule.types';
import { DiagnosticRuleHttpError } from '~/app/services/diagnosticRules/diagnostic-rule-error';

export { DiagnosticRuleHttpError };

const BASE = '/api/v1/diagnostic-rules';

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) message = body.message.join(', ');
      else if (typeof body.message === 'string') message = body.message;
    } catch {
      // non JSON error body
    }
    throw new DiagnosticRuleHttpError(response.status, message);
  }
  return (await response.json()) as T;
}

/**
 * JSON for a TEXT rule, multipart when a document is attached (lists are
 * sent as a JSON array string there). An always-apply rule sends no metadata.
 */
function toBody(
  input: DiagnosticRuleInput,
  extra: Record<string, string> = {},
): { body: BodyInit; headers?: HeadersInit } {
  const alwaysApply = input.kind === 'TEXT' && input.alwaysApply;
  const assetModelIds = alwaysApply ? [] : (input.assetModelIds ?? []);
  const firmwareVersions = alwaysApply
    ? []
    : (input.firmwareVersions ?? [])
        .map((value) => value.trim())
        .filter(Boolean);
  const fields: Record<string, string> = {
    title: input.title,
    description: input.description,
    alwaysApply: String(alwaysApply),
    ...(!alwaysApply && input.manufacturerOrganizationId
      ? { manufacturerOrganizationId: input.manufacturerOrganizationId }
      : {}),
    ...(!alwaysApply && input.standard ? { standard: input.standard } : {}),
    ...extra,
  };
  if (input.file) {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => form.append(key, value));
    if (assetModelIds.length > 0) {
      form.append('assetModelIds', JSON.stringify(assetModelIds));
    }
    if (firmwareVersions.length > 0) {
      form.append('firmwareVersions', JSON.stringify(firmwareVersions));
    }
    form.append('file', input.file);
    return { body: form };
  }
  return {
    body: JSON.stringify({
      ...fields,
      alwaysApply,
      assetModelIds,
      firmwareVersions,
    }),
    headers: { 'Content-Type': 'application/json' },
  };
}

/** `?crossTenantBypass=true` (super admin in another tenant's scope, audited) or ''. */
function bypassQuery(crossTenantBypass: boolean): string {
  return crossTenantBypass ? '?crossTenantBypass=true' : '';
}

export const DiagnosticRuleService = {
  async list(
    scope: DiagnosticRuleScope | null,
    filters: DiagnosticRuleFilters,
    crossTenantBypass = false,
  ): Promise<DiagnosticRule[]> {
    const params = new URLSearchParams();
    if (scope) params.set('organizationId', scope);
    if (crossTenantBypass) params.set('crossTenantBypass', 'true');
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') params.set(key, String(value));
    });
    const response = await fetch(buildApiUrl(`${BASE}?${params}`), {
      credentials: 'include',
    });
    return (await parseJson<{ data: DiagnosticRule[] }>(response)).data;
  },

  async get(
    ruleId: string,
    crossTenantBypass = false,
  ): Promise<DiagnosticRuleDetail> {
    const response = await fetch(
      buildApiUrl(`${BASE}/${ruleId}${bypassQuery(crossTenantBypass)}`),
      { credentials: 'include' },
    );
    return parseJson<DiagnosticRuleDetail>(response);
  },

  /** Charger suggestions come from the scope organization only (global: catalog only). */
  async options(
    scope: DiagnosticRuleScope | null,
    crossTenantBypass = false,
  ): Promise<DiagnosticRuleOptions> {
    const params = new URLSearchParams();
    if (scope) params.set('organizationId', scope);
    if (crossTenantBypass) params.set('crossTenantBypass', 'true');
    const response = await fetch(buildApiUrl(`${BASE}/options?${params}`), {
      credentials: 'include',
    });
    return parseJson<DiagnosticRuleOptions>(response);
  },

  /** `organizationId`: super admin target tenant; omitted = global (or own org for tenants). */
  async create(
    input: DiagnosticRuleInput,
    organizationId?: string,
    crossTenantBypass = false,
  ): Promise<DiagnosticRule> {
    const { body, headers } = toBody(input, {
      kind: input.kind,
      ...(organizationId ? { organizationId } : {}),
    });
    const response = await fetch(
      buildApiUrl(`${BASE}${bypassQuery(crossTenantBypass)}`),
      {
        method: 'POST',
        credentials: 'include',
        body,
        ...(headers ? { headers } : {}),
      },
    );
    return parseJson<DiagnosticRule>(response);
  },

  async update(
    ruleId: string,
    input: DiagnosticRuleInput,
    crossTenantBypass = false,
  ): Promise<DiagnosticRule> {
    const { body, headers } = toBody(input);
    const response = await fetch(
      buildApiUrl(`${BASE}/${ruleId}${bypassQuery(crossTenantBypass)}`),
      {
        method: 'PUT',
        credentials: 'include',
        body,
        ...(headers ? { headers } : {}),
      },
    );
    return parseJson<DiagnosticRule>(response);
  },

  async setActive(
    ruleId: string,
    active: boolean,
    crossTenantBypass = false,
  ): Promise<DiagnosticRule> {
    const response = await fetch(
      buildApiUrl(
        `${BASE}/${ruleId}/${active ? 'activate' : 'deactivate'}${bypassQuery(crossTenantBypass)}`,
      ),
      { method: 'POST', credentials: 'include' },
    );
    return parseJson<DiagnosticRule>(response);
  },

  async reindex(
    ruleId: string,
    crossTenantBypass = false,
  ): Promise<DiagnosticRule> {
    const response = await fetch(
      buildApiUrl(`${BASE}/${ruleId}/reindex${bypassQuery(crossTenantBypass)}`),
      { method: 'POST', credentials: 'include' },
    );
    return parseJson<DiagnosticRule>(response);
  },
};
